import { DefaultLayout } from '@c/layouts';
import SEO from '@c/SEO';
import { ProductSchema, BreadcrumbSchema } from '@c/StructuredData';
import { formatProductItems, getUserDataForDL, trackEvent, viewListing } from '@util/analytics';

import { addCacheTag } from '@vercel/functions';
import {
  addProductToCuratedList,
  bumpProduct,
  expireProduct,
  verifyProduct,
  deleteListing,
  getProductBySlug,
  getMoreFromThisSeller,
  getRelatedProducts,
  removeProductFromCuratedList,
  getCuratedLists,
  updateProductByKey,
  formatListingMetaDescription,
} from '@util/firestore/products';
import { GetServerSideProps } from 'next';
import { ParsedUrlQuery } from 'querystring';
import MarketplaceProductCard, {
  CAROUSEL_CARD_WIDTH,
} from '@c/marketplace/MarketplaceProductCard';
import { CheckCircleIcon, PencilSimpleIcon, PlaneIcon } from '@c/icons';
import BaseMenu from '@c/menus/BaseMenu';
import ProductBanner from '@c/ProductBanner';
import { ProductDetails } from '@c/ProductDetails';
import DynamicToast from '@c/toasts/DynamicToast';
import { Popover } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import Banner from '@ui/Banner';
import Breadcrumb, { BreadcrumbItem } from '@ui/Breadcrumb';
import Button, { ButtonStyles } from '@ui/Button';
import Carousel from '@ui/Carousel';
import MeetTheSellerCarousel from '@ui/MeetTheSellerCarousel';
import PageSectionHeader from '@ui/PageSectionHeader';
import { getRedirectBySlug } from '@util/firestore/redirects'
import { getMasterById } from '@util/firestore/master/master.service.server';
import { toSerializable } from '@util/serializeFirestore';
import { MasterDocument } from '@util/firestore/master';
import { buildCategorySlug, getProductCategories } from '@util/firestore/categories';
import { getUserById } from '@util/firestore/users';
import { ProductDocument } from '@util/types/firestore/products';
import { getProductUrl } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useEffectivePrice, type PublicStoreSale } from 'context/StoreSaleContext';
import { saleInstant } from '@util/storeSale/effectivePrice';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { AuctionBanner } from '@c/products/AuctionBanner';
import useRealTimeBids from '@util/hooks/useRealtimeBids';
import { CuratedListDocument } from '@util/firestore/products';
import { getConditionByName } from '@util/firestore/conditions/conditions.service';
import {
  getProtectionPolicy,
  getReturnPolicy,
  PolicyDocument,
} from '@util/firestore/settings';

interface ProductDetailPageProps {
  product: ProductDocument;
  initialStoreSales: PublicStoreSale[];
  initialStoreSalesNowMs: number;
  master: MasterDocument[];
  breadcrumbs: BreadcrumbItem[];
  conditionDescription?: string | undefined;
  formattedDescription: string;
  isProductIndexable: boolean;
  isVerified: boolean;
  protectionPolicyContent?: string;
  resolvedReturnPolicy?: PolicyDocument | null;
}

interface IParams extends ParsedUrlQuery {
  slug: string;
}

export default function ProductDetailPage({
  product,
  master,
  breadcrumbs,
  conditionDescription,
  formattedDescription,
  isProductIndexable,
  isVerified,
  protectionPolicyContent,
  resolvedReturnPolicy,
}: ProductDetailPageProps) {
  const storePrice = useEffectivePrice(product);
  const displayPrice = storePrice?.applied ? storePrice.unitCents / 100 : product.price;
  const { userDoc } = useAuth();
  const router = useRouter();

  const [hasTrackedView, setHasTrackedView] = useState(false);

  // Reset tracking flag when navigating between product pages (client-side)
  useEffect(() => {
    setHasTrackedView(false);
  }, [product.id]);

  const { data: otherProducts } = useQuery(
    ['products', product.seller_id],
    () => getMoreFromThisSeller(product),
    {
      enabled: !!product.seller_id,
    }
  );

  const { data } = useQuery(
    ['relatedProducts', product.id],
    () => getRelatedProducts(product),
    {
      enabled: !!product.id,
    }
  );

  const { data: sellerDoc } = useQuery(
    ['seller', product.seller_id],
    () => getUserById(product.seller_id),
    {
      enabled: !!product.seller_id,
    }
  );
  const { showToast, showErrorToast } = useToastContext();

  const [lists, setLists] = useState<CuratedListDocument[]>([]);
  useEffect(() => {
    const getAdminCuratedLists = async () => {
      if (userDoc?.roles?.admin !== true) return;

      try {
        const res = await getCuratedLists();
        setLists(res);
      } catch (error) {
        console.error("Error fetching currated lists:", error);
      }
    };

    getAdminCuratedLists();
  }, [userDoc?.roles?.admin]);

  const [curatedLists, setCuratedLists] = useState<string[]>(
    product.curated_lists ?? []
  );

  const [isFeatured, setIsFeatured] = useState<boolean | undefined>(
    product.is_featured
  );

  // Hydration-safe: the auction-over check below (AuctionBanner isOver) reads
  // the wall clock, which differs between the SSR render and the browser. This
  // branch is currently client-only (gated on userDoc + the viewer's own
  // bids), but gate on mount anyway for uniformity with the other time-window
  // checks and to stay safe if the guard ever changes. Comparing against an
  // epoch-0 sentinel is avoided (see ProductDetails) — Date.now() runs only
  // once mounted.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const addToList = async (list: string) => {
    let toastText = '';
    if (curatedLists?.includes(list)) {
      await removeProductFromCuratedList(product.id, list);
      setCuratedLists(curatedLists.filter((l) => l !== list));
      toastText = 'Product successfully removed from the list';
    } else {
      await addProductToCuratedList(product.id, list);
      setCuratedLists([...(curatedLists || []), list]);
      toastText = 'Product successfully added to the list';
    }
    presentToast(toastText);
  };

  const presentToast = (text: string) => {
    showToast(<DynamicToast text={text} />, {
      position: 'bottom-center',
      style: {
        borderRadius: '10px',
        backgroundColor: '#F9E5E5',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: '#C75008',
        width: 'min-content',
      },
    });
  };

  const { realTimeBids, highestBid, usersBids } = useRealTimeBids(
    product.id,
    userDoc?.uid
  );

  // Use the formatted description from SSR (handles UPP notes properly)
  const productDescription = formattedDescription || product.title;
  const productImage = product.images && product.images.length > 0
    ? product.images[0].full
    : '/images/default-product.jpg';

  // Generate category keywords
  const categoryKeywords = product.categories?.join(', ') || '';

  // Product availability for structured data (separate from indexability for SEO)
  const isProductAvailable = !product.is_expired && !product.out_of_stock;

  // track view_item event when the page loads
  useEffect(() => {
    if (!product || product.is_draft || product.out_of_stock || hasTrackedView) return;

    setHasTrackedView(true);

    const categoryBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
    const listId = categoryBreadcrumb?.href?.split('/').pop() || 'products';

    // viewListing fires fbq pixel; capture event_id so trackEvent's dataLayer push
    // carries the same id for Stape CAPI dedup
    viewListing(product, '', userDoc?.uid || '').then((eventId) => {
      trackEvent('view_item', {
        currency: 'USD',
        value: displayPrice,
        items: formatProductItems([{ ...product, price: displayPrice }], listId),
        event_id: eventId,
      }, getUserDataForDL(userDoc));
    });
  }, [product, breadcrumbs, hasTrackedView, displayPrice]);

  // Optimize product title for SEO
  // Remove redundant brand suffix and truncate to 50 chars if needed
  const optimizedTitle = product.title.length > 50
    ? product.title.substring(0, 50).trim() + '...'
    : product.title;

  return (
    // pb on the page root, not the last section: "More from this seller" is
    // conditional, so padding there would leave the seller carousel touching
    // the footer whenever a seller has no other listings.
    <div className="w-full pb-24">
      {/* SEO Implementation - single source of truth for all meta tags */}
      <SEO
        title={optimizedTitle}
        description={productDescription}
        ogImage={productImage}
        ogType="product"
        keywords={[product.brand || '', product.title, ...(product.categories || []), 'camera', 'photography', 'gear'].filter(Boolean)}
        product={{
          price: displayPrice,
          currency: 'USD',
          availability: isProductAvailable ? 'in stock' : 'out of stock',
          brand: product.brand,
          category: categoryKeywords
        }}
        noIndex={!isProductIndexable}
        googleBot={isVerified ? 'index, follow' : undefined}
      />

      {/* Structured Data */}
      <ProductSchema
        name={product.title}
        description={productDescription}
        image={productImage}
        price={displayPrice}
        saleEndsAtMs={storePrice?.applied?.endsAt}
        sku={product.sku}
        brand={product.brand || 'GearFocus'}
        condition={product.condition?.includes('New') ? 'NewCondition' : 'UsedCondition'}
        availability={isProductAvailable ? 'InStock' : 'OutOfStock'}
        url={`https://gearfocus.com/products/${product.slug}`}
        gtin={master[0]?.gtin}
        mpn={master[0]?.mpn}
      />

      {/* Breadcrumb Schema */}
      <BreadcrumbSchema
        items={breadcrumbs.map(b => ({
          name: b.label,
          url: `https://gearfocus.com${b.href}`
        }))}
      />

      {product.is_draft && (
        <Banner color="brand">
          <div className="flex items-center justify-center gap-[0.8rem]">
            <PencilSimpleIcon />
            This is a draft listing.
            {userDoc &&
              (userDoc.uid === product.seller_id || userDoc.roles?.admin) && (
                <Button
                  height="extraSmall"
                  text="( Edit Listing )"
                  type="text"
                  width="extraSmall"
                  href={`/edit-listing/${product.id}`}
                />
              )}
          </div>
        </Banner>
      )}

      {product.on_vacation && (
        <Banner color="brand">
          <div className="flex items-center justify-center gap-[0.8rem]">
            <PlaneIcon />
            This seller is on vacation. Expect a delay in response times.
          </div>
        </Banner>
      )}

      {product.is_auction && highestBid && userDoc && usersBids.length > 0 && (
        <AuctionBanner
          winning={!!highestBid.uid && highestBid.uid === userDoc.uid}
          isOver={mounted && product.end_time! < Date.now()}
        />
      )}

      {!!userDoc?.roles?.admin && (
        <div className="hidden sm:flex z-50 flex-wrap justify-center gap-x-5 pt-[2rem] ">
          <Button
            height="small"
            text={!product.is_expired ? "Expire Listing" : "Renew Listing"}
            type="tertiary"
            width="small"
            onClick={() =>
              expireProduct(product.id, product.is_expired).then(() => {
                presentToast('Product listing updated was successful');
                router.reload();
              })
            }
          />
          <Button
            height="small"
            text={!product.is_verified ? "Verify Listing" : "Unverify Listing"}
            type="tertiary"
            width="small"
            onClick={() =>
              verifyProduct(product.id, product.is_verified).then(() => {
                presentToast('Product listing updated was successful');
                router.reload();
              })
            }
          />
          <Button
            height="small"
            text="Bump"
            type="tertiary"
            width="small"
            onClick={() =>
              bumpProduct(product.id).then(() =>
                presentToast('Product successfully bumped')
              )
            }
          />
          {isFeatured ? (
            <Button
              height="small"
              text="Unfeature"
              type="tertiary"
              width="small"
              onClick={() =>
                updateProductByKey(product.id, 'is_featured', false).then(
                  () => {
                    presentToast('Product successfully unfeatured');
                    setIsFeatured(false);
                  }
                )
              }
            />
          ) : (
            <Button
              height="small"
              text="Feature"
              type="tertiary"
              width="small"
              onClick={() =>
                updateProductByKey(product.id, 'is_featured', true).then(() => {
                  presentToast('Product successfully featured');
                  setIsFeatured(true);
                })
              }
            />
          )}
          <Button
            text="Backend"
            height="small"
            type="tertiary"
            width="small"
            href={`https://console.firebase.google.com/project/gear-focus/firestore/data/~2Fproducts~2F${product.id}`}
            target="_blank"
          />
          <Button
            text="Edit Listing"
            height="small"
            width={'small'}
            type="tertiary"
            href={{
              pathname: '/edit-listing/[id]',
              query: { id: product.id },
            }}
          />

          <Button
            type="tertiary"
            text="Delete Listing"
            width="small"
            height="small"
            // Drafts are always out_of_stock by design (createListingHelpers,
            // moveToDraft), so gating on out_of_stock alone made this
            // permanently disabled for every draft. Only disable it once
            // the listing is actually deleted/sold (out_of_stock + not a
            // draft) — see #1467.
            disabled={product.out_of_stock && !product.is_draft}
            onClick={() => {
              if (confirm('Are you sure you want to delete this listing?')) {
                deleteListing(product.id, product.is_draft)
                  .then((result) => {
                    if (result.archived) {
                      presentToast(
                        'This listing has sales history, so it was archived instead of deleted.'
                      );
                    }
                    // Draft path (delete or archive): always navigate away
                    // — a reload here would hit a 404 on a hard-deleted
                    // draft. Non-draft soft-delete keeps the existing
                    // support-reload behavior.
                    if (product.is_draft || !userDoc?.roles?.support) {
                      router.push('/dashboard/listings');
                    } else {
                      router.reload();
                    }
                  })
                  .catch((e) => {
                    showErrorToast(
                      e instanceof Error
                        ? e.message
                        : 'Delete failed. Please try again.'
                    );
                  });
              }
            }}
          />
          <BaseMenu
            triggerElement={
              <div
                className={ButtonStyles({
                  type: 'tertiary',
                  height: 'small',
                  width: 'small',
                })}
              >
                Add To List
              </div>
            }
          >
            <div className="flex flex-col items-start gap-[1.6rem] rounded-[1rem] bg-brand-white p-[1.6rem] shadow-md">
              <Popover.Button>
                {lists.map((list) => (
                  <Button
                    key={`curated-list-${list.id}`}
                    leadingIcon={
                      curatedLists?.includes(list.id) && <CheckCircleIcon />
                    }
                    text={list.name}
                    type="text"
                    width="small"
                    onClick={() => addToList(list.id)}
                  />
                ))}
              </Popover.Button>

            </div>
          </BaseMenu>
        </div>
      )}

      <div
        className="
          w-full max-w-default
          mx-auto
          flex flex-col items-center 
          justify-center gap-[1.6rem] 
          lg:mt-[3rem]
          px-[2rem] md:px-[4rem] xl:px-default-padding-x
        "
      >
        <div className="hidden w-full lg:block">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Products', href: '/products' },
              { label: product.categories?.[0] || 'Photography', href: `/c/${(product.categories?.[0] || 'photography').toLowerCase().replace(/\s+/g, '-')}` },
              { label: product.title, href: `/products/${product.slug}` }
            ]}
          />
        </div>

        <ProductDetails
          product={product}
          master={master}
          breadcrumbs={breadcrumbs}
          bids={realTimeBids}
          userBids={usersBids}
          highestBid={highestBid}
          conditionDescription={conditionDescription}
          protectionPolicyContent={protectionPolicyContent}
          resolvedReturnPolicy={resolvedReturnPolicy}
        />
      </div>

      <ProductBanner />
      <MeetTheSellerCarousel seller_id={product.seller_id} />

      {/* More from this seller carousel + header */}
      {!!otherProducts?.length && (
        <div className="mx-auto mt-16 max-w-default px-2 text-center sm:px-default-padding-x sm:text-left">
          <PageSectionHeader
            title="More from this seller"
            href={`/shop-seller/${sellerDoc?.username_slug}`}
            linkText="View All"
          ></PageSectionHeader>
          <Carousel
            items={
              otherProducts.map((product) => (
                <MarketplaceProductCard
                  key={product.id}
                  item={product}
                  className={CAROUSEL_CARD_WIDTH}
                />
              )) ?? []
            }
          />
        </div>
      )}
    </div>
  );
}

ProductDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<
  ProductDetailPageProps
> = async (context) => {
    // NOTE: the cacheable Cache-Control header is intentionally NOT set here at the top.
    // It is applied only on the successful render path near the end of this function, so the
    // early redirect branches (not-found / expired / draft) never inherit a public s-maxage and
    // therefore never get edge-cached. See the render-path setHeader below.

    const { slug } = context.params as IParams;

    const { results: product } = await getProductBySlug({ slug });

  // Ensure gearMaster is an array (legacy products may have it as a string)
  const gearMasterArray = product?.gearMaster
    ? (Array.isArray(product.gearMaster) ? product.gearMaster : [product.gearMaster])
    : [];

  const master = gearMasterArray.length > 0
    ? (await Promise.all(
      gearMasterArray.map((masterId) => getMasterById(masterId))
    )).filter((m): m is MasterDocument => m !== null)
    : [];

  if (!product) {
    const { results: redirects } = await getRedirectBySlug({ slug: `/products/${slug}` });

    if (redirects) {
      return {
        redirect: {
          destination: redirects.destination,
          permanent: redirects.permanent
        }
      }
    }

    return {
      redirect: {
        destination: `/products`,
        permanent: false
      }
    }
  }

  // Redirect expired listings to the main products page
  // Uses 301 (permanent) if no date_updated or older than 6 months
  // Uses 302 (temporary) if recently expired, in case seller renews
  if (product.is_expired) {
    const sixMonthsAgo = Date.now() - (6 * 30 * 24 * 60 * 60 * 1000);
    const isPermanent = !product.date_updated || product.date_updated < sixMonthsAgo;

    return {
      redirect: {
        destination: '/products',
        permanent: isPermanent
      }
    }
  }

  // Redirect draft listings to the matching GearMaster page (or /products as fallback)
  // Allow the seller who owns the draft or admins to preview it
  if (product.is_draft) {
    // A draft URL must never sit in the shared CDN cache — neither the owner/admin preview render
    // nor the anonymous !canViewDraft redirect. Cache entries are keyed by URL (not cookies), so a
    // publicly-cached redirect would be served to the owner too and deny them their own preview.
    // Set no-store as soon as is_draft is known, before either branch (per Codex review, PR #1418).
    context.res.setHeader('Cache-Control', 'private, no-store');

    let canViewDraft = false;
    try {
      const token = context.req.cookies?.['__session'] || context.req.cookies?.['auth'];
      if (token) {
        const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
        const uid = payload?.sub;
        if (uid === product.seller_id) {
          canViewDraft = true;
        } else if (uid) {
          const viewer = await getUserById(uid);
          canViewDraft = !!viewer?.roles?.admin;
        }
      }
    } catch (_) {}

    if (!canViewDraft) {
      const masterSlug = master.length > 0 && master[0].slug
        ? `/m/${master[0].slug}`
        : '/products';

      return {
        redirect: {
          destination: masterSlug,
          permanent: false
        }
      }
    }
  }

  if (!product.description && product.description_html)
    product.description = product.description_html
      .replace(/(<([^>]+)>)/gi, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&');

  const categories = await getProductCategories(product.categories);

  const condition = await getConditionByName(product.condition);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'All Products',
      href: '/products',
    },
  ];

  for (const category of categories) {
    if (!category) continue;

    const href = await buildCategorySlug(categories, category);
    breadcrumbs.push({
      label: category?.name,
      href: href,
    });
  }

  breadcrumbs.push({
    label: product.title,
    href: getProductUrl(product.slug),
    current: true,
    currentCss: 'text-brand-black',
  });

  // hide the sellers address
  delete product.shipment;

  // Use stored meta_description if available (AI-generated), otherwise fall back to on-the-fly generation
  const description = product.meta_description || formatListingMetaDescription(product.description || "", product.description_html || product.title)

  // Indexability: only index products that are available, not draft, not expired, AND verified
  const isProductIndexable = !product.out_of_stock && !product.is_draft && !product.is_expired && product.is_verified

  // Pre-fetch buyer-protection and the seller's return policy for the modals.
  // A settings lookup failure must not prevent rendering the product page.
  const [protectionPolicySettled, sellerSettled] = await Promise.allSettled([
    getProtectionPolicy(),
    getUserById(product.seller_id),
  ]);
  const protectionPolicyContent =
    protectionPolicySettled.status === 'fulfilled' ? protectionPolicySettled.value : undefined;
  const seller = sellerSettled.status === 'fulfilled' ? sellerSettled.value : null;
  let resolvedReturnPolicy: PolicyDocument | null = null;
  if (seller?.return_policy) {
    try {
      resolvedReturnPolicy = (await getReturnPolicy(seller.return_policy)) ?? null;
    } catch {
      resolvedReturnPolicy = null;
    }
  }

  // The sale overlay is per seller, so one tag purges all of that seller's
  // product pages when a sale is created or cancelled.
  addCacheTag(`product-${product.id}`);
  addCacheTag(`seller-${product.seller_id}`);
  addCacheTag('products');

  const saleNowMs = Date.now();
  let initialStoreSales: PublicStoreSale[] = [];
  try {
    const { getStoreSaleConfig, getStoreSalesForSellers } = await import('@util/storeSale/server');
    const [config, sales] = await Promise.all([
      getStoreSaleConfig(),
      getStoreSalesForSellers([product.seller_id]),
    ]);
    const sale = sales.get(product.seller_id);
    const startsAt = sale ? saleInstant(sale.starts_at) : NaN;
    const endsAt = sale ? saleInstant(sale.ends_at) : NaN;
    if (config.enabled && (!config.allowlist || config.allowlist.includes(product.seller_id)) &&
        sale && sale.cancelled_at == null && typeof sale.sale_id === 'string' &&
        Number.isInteger(sale.percent_off) && Number.isFinite(startsAt) &&
        Number.isFinite(endsAt) && endsAt > saleNowMs) {
      initialStoreSales = [{
        seller_id: product.seller_id,
        sale_id: sale.sale_id,
        percent_off: sale.percent_off,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      }];
    }
  } catch (error) {
    // A sale lookup failure displays the listing price. Checkout remains the
    // price authority and will refuse a stale cart item.
    console.error('Could not read store sale for product page', error);
  }

  // Apply the cacheable header ONLY on this successful render path — never on the redirect
  // branches above (a redirect returned from getServerSideProps with a public s-maxage gets
  // edge-cached by URL, and Next ignores res Cache-Control on the redirect body so it can't be
  // overridden there). Guard on !is_draft: a draft that reaches here is an owner/admin preview,
  // which must keep the `private, no-store` set earlier and must never be publicly cached.
  if (!product.is_draft) {
    const nextBoundary = initialStoreSales.flatMap((sale) => [
      Date.parse(sale.starts_at), Date.parse(sale.ends_at),
    ]).filter((time) => time > saleNowMs).sort((a, b) => a - b)[0];
    const cacheSeconds = nextBoundary
      ? Math.max(0, Math.min(600, Math.floor((nextBoundary - saleNowMs) / 1000)))
      : 600;
    context.res.setHeader(
      'Cache-Control',
      nextBoundary
        ? `public, max-age=0, s-maxage=${cacheSeconds}, stale-while-revalidate=0`
        : 'public, max-age=0, s-maxage=600, stale-while-revalidate=1800'
    );
  }

  return {
    // Firestore docs arrive raw (the collection refs are type casts, not
    // converters), so any Timestamp on them — e.g. `gearmaster_linked_at`,
    // written as a Timestamp by the auto-link-v2 job and absent from the
    // schema — would fail Next's JSON serialization here.
    props: toSerializable({
      product,
      initialStoreSales,
      initialStoreSalesNowMs: saleNowMs,
      master,
      breadcrumbs,
      conditionDescription: condition?.description || "",
      formattedDescription: description,
      isProductIndexable,
      isVerified: product.is_verified ?? false,
      protectionPolicyContent,
      resolvedReturnPolicy,
    }),
  };
};
